import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react'
import s from './AnchorWrapper.module.scss'

/**
 * Stateful anchor wrapper for AI draft popovers. Exposes imperative methods
 * to show/hide the anchor when draft state changes. Renders a highlight overlay
 * for the latest draft.
 */

export interface AnchorWrapperHandle {
  hideDraft: () => void
  showDraft: () => void
}

interface AnchorWrapperProps {
  isLatestDraft: boolean
  initiallyVisible?: boolean
}

const AnchorWrapper = forwardRef<AnchorWrapperHandle, AnchorWrapperProps>(
  ({ isLatestDraft, initiallyVisible = true }, ref) => {
    const [isVisible, setIsVisible] = useState(initiallyVisible)
    const [shouldHighlight, setShouldHighlight] = useState(isLatestDraft)

    useEffect(() => {
      if (!isLatestDraft) {
        return
      }

      const timer = setTimeout(() => setShouldHighlight(false), 3000)
      return () => clearTimeout(timer)
    }, [isLatestDraft])

    useImperativeHandle(ref, () => ({
      hideDraft: () => {
        setIsVisible(false)
      },
      showDraft: () => {
        setIsVisible(true)
      }
    }))

    if (!isVisible) {
      return null
    }

    return (
      <>
        {shouldHighlight && <div className={s.highlightOverlay} data-test="highlight-overlay" />}
        <div className={s.anchor} data-latest-draft={isLatestDraft} data-test="anchor" />
      </>
    )
  }
)

AnchorWrapper.displayName = 'AnchorWrapper'

export default AnchorWrapper
